import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft, Loader2, Package2, Printer, Trash2, Plus,
  FileText, Tag, Globe, PackageCheck, AlertCircle, CheckCircle2,
  X as XIcon,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api, formatMoney } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'

interface Volume {
  id: number
  numero: number
  peso_kg: number | null
  altura_cm: number | null
  largura_cm: number | null
  comprimento_cm: number | null
  observacao: string | null
}

const CANAL_LABELS: Record<string, string> = {
  balcao: 'PDV', digitado: 'Digitado',
  mercado_livre: 'Mercado Livre', tray: 'Tray', shopee: 'Shopee',
}

export default function EmbalagemPage() {
  const { id } = useParams<{ id: string }>()
  const nav = useNavigate()
  const qc = useQueryClient()
  const pid = Number(id)

  // Carrega pedido
  const { data: pedido, isLoading } = useQuery<any>({
    queryKey: ['pedido', pid],
    queryFn: async () => (await api.get(`/vendas/${pid}`)).data,
  })

  // Carrega volumes
  const { data: volumes = [] } = useQuery<Volume[]>({
    queryKey: ['pedido-volumes', pid],
    queryFn: async () => (await api.get(`/vendas/pedidos/${pid}/volumes`)).data,
    enabled: !!pedido,
  })

  // Criar volume
  const [peso, setPeso] = useState('')
  const [altura, setAltura] = useState('')
  const [largura, setLargura] = useState('')
  const [comprimento, setComprimento] = useState('')

  const criarVol = useMutation({
    mutationFn: async () => (await api.post(`/vendas/pedidos/${pid}/volumes`, {
      peso_kg: peso ? parseFloat(peso) : null,
      altura_cm: altura ? parseFloat(altura) : null,
      largura_cm: largura ? parseFloat(largura) : null,
      comprimento_cm: comprimento ? parseFloat(comprimento) : null,
    })).data,
    onSuccess: () => {
      toast.success('Volume adicionado')
      setPeso(''); setAltura(''); setLargura(''); setComprimento('')
      qc.invalidateQueries({ queryKey: ['pedido-volumes', pid] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const deletarVol = useMutation({
    mutationFn: async (vid: number) => api.delete(`/vendas/pedidos/${pid}/volumes/${vid}`),
    onSuccess: () => {
      toast.success('Volume removido')
      qc.invalidateQueries({ queryKey: ['pedido-volumes', pid] })
    },
  })

  const concluir = useMutation({
    mutationFn: async () => (await api.post(`/vendas/pedidos/${pid}/concluir-embalagem`, {})).data,
    onSuccess: () => {
      toast.success('Embalagem concluída → Aguardando NF')
      qc.invalidateQueries({ queryKey: ['pedidos'] })
      qc.invalidateQueries({ queryKey: ['pedido', pid] })
      nav('/vendas')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  // Impressões — abrem em nova aba
  function abrirPDF(path: string) {
    const token = localStorage.getItem('pain_token')
    // Para abrir PDF com auth, o jeito mais simples é fetch + blob
    fetch(`/api${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        return r.blob()
      })
      .then(blob => {
        const url = URL.createObjectURL(blob)
        window.open(url, '_blank')
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      })
      .catch(e => toast.error(`Falha ao gerar PDF: ${e.message}`))
  }

  if (isLoading || !pedido) {
    return (
      <div className="flex items-center justify-center h-96 text-ink-400">
        <Loader2 className="animate-spin mr-2" /> Carregando...
      </div>
    )
  }

  const canal = pedido.canal_origem
  const canalLabel = CANAL_LABELS[canal] || canal
  const isMarketplace = ['mercado_livre', 'tray', 'shopee'].includes(canal)
  const podeEditar = pedido.status === 'embalagem'

  return (
    <div className="min-h-screen bg-ink-50">
      {/* HEADER PRETO */}
      <div className="bg-ink-900 text-white px-6 py-3 flex items-center justify-between sticky top-0 z-40 shadow-lg">
        <div className="flex items-center gap-3">
          <button onClick={() => nav('/vendas')}
            className="p-2 rounded hover:bg-ink-800">
            <ArrowLeft size={18} />
          </button>
          <div>
            <div className="text-xs text-ink-400 uppercase tracking-wider">Embalagem</div>
            <div className="font-semibold">Pedido #{pedido.numero} — {canalLabel}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-ink-300">
            Status: <strong className="text-white uppercase">{pedido.status}</strong>
          </span>
          {podeEditar && (
            <button
              disabled={concluir.isPending}
              onClick={() => {
                if (volumes.length === 0) {
                  if (!confirm('Concluir sem volumes? Você precisa pelo menos 1 volume pra imprimir etiqueta.'))
                    return
                }
                concluir.mutate()
              }}
              className="text-sm px-4 py-2 rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 font-medium flex items-center gap-1.5">
              {concluir.isPending
                ? <><Loader2 size={14} className="animate-spin" /> Concluindo...</>
                : <><PackageCheck size={14} /> Concluir embalagem</>
              }
            </button>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-4 space-y-4">
        {/* DADOS DO PEDIDO */}
        <div className="bg-white rounded-xl shadow p-4">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs text-ink-500 uppercase">Total</div>
              <div className="text-lg font-semibold text-brand-600">{formatMoney(pedido.total)}</div>
            </div>
            <div>
              <div className="text-xs text-ink-500 uppercase">Itens</div>
              <div>{pedido.itens?.length || 0} produto(s)</div>
            </div>
            <div>
              <div className="text-xs text-ink-500 uppercase">Canal</div>
              <div className="flex items-center gap-1">
                {isMarketplace ? <Globe size={14} /> : <Package2 size={14} />}
                {canalLabel}
              </div>
            </div>
          </div>
        </div>

        {/* VOLUMES */}
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="px-4 py-3 border-b border-ink-100 flex items-center justify-between">
            <div className="text-sm font-semibold text-ink-700 flex items-center gap-2">
              <Package2 size={14} /> Volumes ({volumes.length})
            </div>
          </div>

          {volumes.length === 0 ? (
            <div className="p-6 text-center text-sm text-ink-400">
              Nenhum volume cadastrado ainda
            </div>
          ) : (
            <div className="divide-y divide-ink-100">
              {volumes.map(v => (
                <div key={v.id} className="px-4 py-2.5 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center flex-shrink-0 font-semibold">
                    {v.numero}
                  </div>
                  <div className="flex-1 text-sm">
                    <div className="font-medium">Volume {v.numero}</div>
                    <div className="text-xs text-ink-500">
                      {v.peso_kg ? `${v.peso_kg} kg` : '— sem peso —'}
                      {v.altura_cm && v.largura_cm && v.comprimento_cm &&
                        ` · ${v.altura_cm}×${v.largura_cm}×${v.comprimento_cm} cm`}
                    </div>
                  </div>
                  {podeEditar && (
                    <button onClick={() => pedirExclusao({ descricao: `o Volume ${v.numero}`, onConfirm: () => deletarVol.mutate(v.id) })}
                      className="p-2 hover:bg-red-50 text-red-600 rounded">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Form de adicionar volume */}
          {podeEditar && (
            <div className="p-4 bg-ink-50 border-t border-ink-100">
              <div className="grid grid-cols-4 gap-2 mb-2">
                <input type="number" step="0.01" placeholder="Peso (kg)"
                  value={peso} onChange={e => setPeso(e.target.value)}
                  className="input input-sm" />
                <input type="number" placeholder="Altura (cm)"
                  value={altura} onChange={e => setAltura(e.target.value)}
                  className="input input-sm" />
                <input type="number" placeholder="Largura (cm)"
                  value={largura} onChange={e => setLargura(e.target.value)}
                  className="input input-sm" />
                <input type="number" placeholder="Compr. (cm)"
                  value={comprimento} onChange={e => setComprimento(e.target.value)}
                  className="input input-sm" />
              </div>
              <button onClick={() => criarVol.mutate()}
                disabled={criarVol.isPending}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium py-2 rounded disabled:opacity-50 flex items-center justify-center gap-1.5">
                {criarVol.isPending
                  ? <><Loader2 size={14} className="animate-spin" /> Adicionando...</>
                  : <><Plus size={14} /> Adicionar volume</>
                }
              </button>
              <p className="text-xs text-ink-500 mt-1.5">
                💡 Peso e dimensões são opcionais mas recomendados pra etiquetas precisas
              </p>
            </div>
          )}
        </div>

        {/* IMPRESSÕES */}
        <div className="bg-white rounded-xl shadow">
          <div className="px-4 py-3 border-b border-ink-100">
            <div className="text-sm font-semibold text-ink-700 flex items-center gap-2">
              <Printer size={14} /> Impressões
            </div>
          </div>

          <div className="p-4 space-y-3">
            {/* 1. Imprimir pedido */}
            <BotaoImpressao
              icon={FileText}
              cor="bg-blue-600 hover:bg-blue-700"
              label="📋 Imprimir pedido"
              sublabel="Romaneio A4 — 1 folha por pedido"
              onClick={() => abrirPDF(`/vendas/pedidos/${pid}/imprimir-pedido`)}
            />

            {/* 2. Etiqueta própria — sempre disponível */}
            <div className="border border-ink-200 rounded-lg p-3 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-ink-700">
                <Tag size={14} /> Etiqueta própria
                <span className="text-xs text-ink-400">
                  {volumes.length > 0
                    ? `(${volumes.length} etiqueta${volumes.length > 1 ? 's' : ''})`
                    : '(1 etiqueta genérica)'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => abrirPDF(`/vendas/pedidos/${pid}/etiqueta-propria?formato=etiqueta`)}
                  className="bg-slate-700 hover:bg-slate-800 text-white text-sm font-medium py-2 rounded">
                  📦 10×15 cm (térmica)
                </button>
                <button onClick={() => abrirPDF(`/vendas/pedidos/${pid}/etiqueta-propria?formato=a4`)}
                  className="bg-slate-600 hover:bg-slate-700 text-white text-sm font-medium py-2 rounded">
                  📄 A4 (laser comum)
                </button>
              </div>
            </div>

            {/* 3. Etiqueta marketplace — contextual */}
            {isMarketplace && (
              <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium text-amber-900">
                  <Globe size={14} /> Etiqueta {canalLabel}
                </div>
                <div className="text-xs text-amber-800">
                  {pedido.marketplace_shipment_id
                    ? '✅ Shipment ID sincronizado — baixe a etiqueta'
                    : pedido.status === 'aguardando_nf' || pedido.status === 'faturado'
                      ? '⏳ Aguardando sincronização da NF com o marketplace'
                      : '⚠️ Emita a NF primeiro — o marketplace só libera a etiqueta depois'
                  }
                </div>
                <button
                  onClick={() => abrirPDF(`/vendas/pedidos/${pid}/etiqueta-marketplace`)}
                  disabled={!pedido.marketplace_shipment_id}
                  className="w-full bg-amber-600 hover:bg-amber-700 disabled:bg-ink-300 disabled:cursor-not-allowed text-white text-sm font-medium py-2 rounded">
                  📥 Baixar etiqueta do {canalLabel}
                </button>
              </div>
            )}

            {/* Info DANFE */}
            <div className="mt-3 bg-ink-50 rounded-lg p-3 flex items-start gap-2 text-xs text-ink-600">
              <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
              <div>
                <strong>DANFE simplificada</strong> estará disponível depois que você emitir a NF-e
                (no próximo passo: "Aguardando NF" → "Marcar faturado").
              </div>
            </div>
          </div>
        </div>

        {!podeEditar && (
          <div className="bg-emerald-50 border border-emerald-300 rounded-xl p-4 text-emerald-900 text-sm">
            <CheckCircle2 size={16} className="inline mr-1" />
            Pedido já avançou da embalagem — use esta tela apenas para reimprimir.
          </div>
        )}
      </div>
    </div>
  )
}


function BotaoImpressao({ icon: Icon, cor, label, sublabel, onClick }: {
  icon: any; cor: string; label: string; sublabel?: string; onClick: () => void
}) {
  return (
    <button onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-white text-sm font-medium transition-colors ${cor}`}>
      <Icon size={16} />
      <div className="text-left flex-1">
        <div>{label}</div>
        {sublabel && <div className="text-[11px] font-normal opacity-90">{sublabel}</div>}
      </div>
    </button>
  )
}
